// MerchantDetail — single merchant, wired to GET /v1/merchants/:id and
// GET /v1/merchants/:id/bank-accounts (Phase 06.1).
//
// Reachable from the admin Merchants list. RequireMerchantScope on the
// backend admits admins for any :id and members for their own merchant.
import { useState, useEffect, type FormEvent, type ReactNode } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { AddBeneficiaryModal } from "@/components/AddBeneficiaryModal";
import { PayoutLimitsCard } from "@/components/PayoutLimitsCard";
import { RoutingRulesCard } from "@/components/RoutingRulesCard";
import { formatDateTime, fmtCcyMinor } from "@/utils/format";
import { apiErrorMessage } from "@/utils/apiError";
import { providerLabel } from "@/utils/providerLabel";
import { useMerchant } from "@/queries/useMerchant";
import {
  listBankAccounts,
  verifyBankAccount,
  type BankAccount,
  type VerifyResult,
} from "@/api/bankAccounts";
import {
  updateMerchant,
  deleteMerchant,
  type Merchant,
  type DeleteMerchantResult,
  type BalanceDisplay,
} from "@/api/merchants";
import { listMemberships } from "@/api/memberships";
import { listApiKeys } from "@/api/apiKeys";
import { listPayouts } from "@/api/payouts";
import { listBeneficiaries } from "@/api/beneficiaries";
import { listLedgerEntries } from "@/api/merchantLedger";
import { LedgerEntryRow } from "@/components/Ledger/LedgerEntryRow";
import { useAuth, isAdminRole, isSuperadminRole } from "@/store/auth";
import { useUI } from "@/store/ui";

// Cap each MerchantDetail section at this row count and surface the
// rest behind a "View all →" link to the dedicated section page. The
// drill-down was previously rendered as a full table inline which made
// the page unscrollable as a merchant grew past ~50 payouts /
// beneficiaries. Each section keeps its own "Loading…" / "0 rows" /
// permission-aware emptyState — only the populated branch caps.
const SECTION_PREVIEW = 10;

// ─── Generic resource section — loading / error / empty / table states ───────

function ResourceCard({
  title,
  isLoading,
  isError,
  count,
  emptyText,
  action,
  viewAll,
  children,
}: {
  title: string;
  isLoading: boolean;
  isError: boolean;
  count: number;
  emptyText: string;
  action?: ReactNode;
  /** Footer "View all →" affordance — rendered only when the visible
      slice is smaller than the full count. The handler is left to the
      caller because it needs to set the merchant switcher BEFORE
      navigating to the dedicated section page (otherwise the
      destination page would render whichever merchant the switcher
      was pointed at last). */
  viewAll?: { label: string; onClick: () => void };
  children: ReactNode;
}) {
  return (
    <div className="card" style={{ marginTop: "var(--card-gap)" }}>
      <div className="card-head">
        <h3>{title}</h3>
        <div className="spacer" />
        <span className="dim" style={{ fontSize: 11 }}>
          {isLoading ? "Loading…" : `${count}`}
        </span>
        {action}
      </div>
      {isLoading ? (
        <div className="empty">
          <div className="dim" style={{ fontSize: 13 }}>
            Loading…
          </div>
        </div>
      ) : isError ? (
        <div className="empty">
          <p>Couldn't load {title.toLowerCase()} for this merchant.</p>
        </div>
      ) : count === 0 ? (
        <div className="empty">
          <p>{emptyText}</p>
        </div>
      ) : (
        <>
          {children}
          {viewAll && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "8px 12px",
                borderTop: "1px solid var(--hair)",
              }}
            >
              <button
                className="btn ghost sm"
                type="button"
                onClick={viewAll.onClick}
                title={viewAll.label}
              >
                {viewAll.label} <I.ChevronRight size={11} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Edit merchant modal ──────────────────────────────────────────────────────

/**
 * Choice — one explanatory option in a radio group.
 *
 * Every setting in this modal used to be a bare <select> whose <option>
 * text was the DB enum token ("active", "live_paysmart"). Operators had to
 * already know the schema to predict what a setting would do. A Choice
 * shows the consequence next to the name, which is what they're actually
 * choosing between. The wire value is unchanged — only the presentation.
 */
function Choice({
  name,
  value,
  current,
  onPick,
  title,
  children,
}: {
  name: string;
  value: string;
  current: string;
  onPick: (v: string) => void;
  title: string;
  children: ReactNode;
}) {
  const on = current === value;
  return (
    <label className={"choice" + (on ? " on" : "")}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={on}
        onChange={() => onPick(value)}
      />
      <span className="choice-text">
        <span className="choice-title">{title}</span>
        <span className="choice-desc">{children}</span>
      </span>
    </label>
  );
}

function EditMerchantModal({
  merchant,
  open,
  onClose,
}: {
  merchant: Merchant;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState(merchant.display_name);
  const [status, setStatus] = useState(merchant.status);
  // Superadmin per-merchant API toggle (backend migration 027). Drives
  // both stk_… key auth and the panel's API keys section. NEW merchants
  // default to false on the backend; existing rows were backfilled to
  // true. Treat undefined-on-the-wire as false defensively.
  const [apiEnabled, setApiEnabled] = useState(merchant.api_enabled ?? false);
  // Balance-display lever (backend migration 030, issue #2). The DB CHECK
  // constraint pins live_paysmart / hidden / ledger; the backend rejects
  // any other value with 400 balance_display_invalid. Default the picker
  // to live_paysmart (live bank balance) for rows that never had the
  // column set — the wire token stays as the historic DB enum value.
  const [balanceDisplay, setBalanceDisplay] = useState<BalanceDisplay>(
    merchant.balance_display ?? "live_paysmart",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Balance source is SUPERADMIN-only (backend rejects it per-field with
  // balance_display_superadmin_only). Which balance a merchant is shown is a
  // disclosure decision: 'hidden' conceals a real bank balance and 'ledger'
  // shows a number we compute rather than one the bank confirms. Everything
  // else on this form stays admin-editable.
  const isSuperadmin = useAuth((s) => isSuperadminRole(s.user?.role));

  // Re-seed the fields from the merchant whenever the modal (re)opens.
  // (Migration 034 moved auto_approve off merchants onto bank_accounts.
  // The per-rail flag is edited on the rail card on /banks now.)
  useEffect(() => {
    if (open) {
      setDisplayName(merchant.display_name);
      setStatus(merchant.status);
      setApiEnabled(merchant.api_enabled ?? false);
      setBalanceDisplay(merchant.balance_display ?? "live_paysmart");
      setError(null);
    }
  }, [open, merchant]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const name = displayName.trim();
    if (name === "") {
      setError("Display name can't be empty.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateMerchant(merchant.id, {
        display_name: name,
        status,
        api_enabled: apiEnabled,
        // Omitted for non-superadmins. Sending it unchanged would still trip
        // the per-field gate, so a plain admin renaming a merchant would get
        // a 403 about a setting they never touched.
        ...(isSuperadmin ? { balance_display: balanceDisplay } : {}),
      });
      // Prefix, NOT ["merchant", merchant.id].
      //
      // useMerchant keys on the URL identifier, which is the SLUG on every
      // link in this app ("/merchants/4rabet"), while this had the numeric
      // id. Two different cache entries, so the one the page actually reads
      // was never invalidated: the save landed, the UI kept showing the old
      // value, and re-opening the modal re-seeded the stale record. It read
      // as "my change didn't save" for every field on this form, not just
      // balance source.
      //
      // ["merchant"] prefix-matches every per-identifier entry, so id- and
      // slug-keyed caches both refresh and the duality can't bite again.
      await qc.invalidateQueries({ queryKey: ["merchant"] });
      await qc.invalidateQueries({ queryKey: ["merchants"] });
      // Refresh /v1/me too — the per-merchant api_enabled flag drives
      // the sidebar entry for the merchant's own operators, so they
      // pick up the change on next nav without a manual reload.
      await qc.invalidateQueries({ queryKey: ["me"] });
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save — check your access and try again."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={saving}
      title="Edit merchant"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="edit-merchant-form"
            disabled={saving || displayName.trim() === ""}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </>
      }
    >
      <form id="edit-merchant-form" onSubmit={handleSubmit} className="stack-24">
        <div className="stack-8">
          <div className="eyebrow">Name</div>
          <div className="field">
            <label className="label" htmlFor="edit-merchant-name">
              Display name
            </label>
            <input
              id="edit-merchant-name"
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoFocus
            />
            <div className="dim" style={{ fontSize: 11 }}>
              Shown across the panel and on receipts. Safe to change at any
              time — it isn't used to identify the merchant anywhere.
            </div>
          </div>

          <div className="field">
            <label className="label" id="edit-merchant-slug-label">
              Permanent ID
            </label>
            <div
              className="readonly-value"
              aria-labelledby="edit-merchant-slug-label"
            >
              {merchant.slug} · mer_{merchant.id}
            </div>
            <div className="dim" style={{ fontSize: 11 }}>
              Used in URLs, API paths and every audit record. Can't be
              changed.
            </div>
          </div>
        </div>

        <div className="stack-8">
          <div className="eyebrow">Can this merchant pay out?</div>
          <div className="choices" role="radiogroup" aria-label="Status">
            <Choice
              name="edit-merchant-status"
              value="active"
              current={status}
              onPick={setStatus}
              title="Active"
            >
              Normal operation. Payouts and approvals go through.
            </Choice>
            <Choice
              name="edit-merchant-status"
              value="suspended"
              current={status}
              onPick={setStatus}
              title="Suspended — paused, reversible"
            >
              New payouts and approvals are rejected and a banner appears
              across their panel. History stays readable. Set back to Active
              to resume.
            </Choice>
            <Choice
              name="edit-merchant-status"
              value="closed"
              current={status}
              onPick={setStatus}
              title="Closed — wound down"
            >
              Blocks the same things as Suspended, but reads as a permanent
              end of the relationship. This is the state a removed merchant
              lands in when it still has history.
            </Choice>
          </div>
          {status !== "active" && merchant.status === "active" && (
            <div
              role="status"
              style={{
                fontSize: 12,
                color: "var(--warn, #b42318)",
                lineHeight: 1.5,
              }}
            >
              Payouts in flight are unaffected, but this merchant won't be
              able to start or approve any new payout once you save.
            </div>
          )}
        </div>

        <div className="stack-8">
          <div className="eyebrow">API access</div>
          <div
            className="choices"
            role="radiogroup"
            aria-label="API access"
          >
            <Choice
              name="edit-merchant-api"
              value="on"
              current={apiEnabled ? "on" : "off"}
              onPick={(v) => setApiEnabled(v === "on")}
              title="On"
            >
              Owners and operators see the API keys screen and can issue
              keys. Existing keys work.
            </Choice>
            <Choice
              name="edit-merchant-api"
              value="off"
              current={apiEnabled ? "on" : "off"}
              onPick={(v) => setApiEnabled(v === "on")}
              title="Off"
            >
              The API keys screen is hidden and any request made with one of
              their keys is rejected. Keys aren't deleted — turning this back
              on makes every existing key work again.
            </Choice>
          </div>
        </div>

        {isSuperadmin && (
        <div className="stack-8">
          <div className="eyebrow">Balance shown on their dashboard</div>
          <div
            className="choices"
            role="radiogroup"
            aria-label="Balance source"
          >
            <Choice
              name="edit-merchant-balance"
              value="live_paysmart"
              current={balanceDisplay}
              onPick={(v) => setBalanceDisplay(v as BalanceDisplay)}
              title="Live bank balance"
            >
              Reads the real balance from this merchant's bank rail each
              time the dashboard loads. Needs an active rail — if
              there isn't one, the card reads "not connected".
            </Choice>
            <Choice
              name="edit-merchant-balance"
              value="ledger"
              current={balanceDisplay}
              onPick={(v) => setBalanceDisplay(v as BalanceDisplay)}
              title="Manual ledger"
            >
              What you've recorded on the Ledger screen, minus everything
              they've successfully paid out. Use this when you hold their
              float yourself. Until you add a first entry the card says the
              ledger isn't set up rather than showing ₹0.00.
            </Choice>
            <Choice
              name="edit-merchant-balance"
              value="hidden"
              current={balanceDisplay}
              onPick={(v) => setBalanceDisplay(v as BalanceDisplay)}
              title="Hidden"
            >
              No balance card and no call to the bank. The right choice while
              a merchant is still being onboarded, or when they only collect
              through Buckzy.
            </Choice>
          </div>
          {balanceDisplay === "ledger" && (
            <Link
              to={`/merchants/${merchant.slug || merchant.id}/ledger`}
              className="btn ghost sm"
              style={{ alignSelf: "flex-start" }}
            >
              Open ledger <I.ArrowRight size={12} />
            </Link>
          )}
        </div>
        )}

        {error && (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ─── Remove merchant confirmation modal ──────────────────────────────────────

/**
 * RemoveMerchantModal — admin confirmation flow for DELETE /v1/merchants/:id.
 *
 * Reality of multi-tenant ops: every dependent table (bank accounts, payouts,
 * beneficiaries, API keys, …) references `merchants(id)` with ON DELETE
 * RESTRICT. The backend handles the fallback transparently — either hard-
 * deleting a clean merchant (just created by mistake) or soft-closing one
 * with history (status='closed' + disabled_at). This modal frames both
 * outcomes for the operator before they confirm.
 */
function RemoveMerchantModal({
  merchant,
  open,
  onClose,
}: {
  merchant: Merchant;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const addToast = useUI((s) => s.addToast);
  const [confirmSlug, setConfirmSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setConfirmSlug("");
      setError(null);
    }
  }, [open]);

  const slugMatches = confirmSlug.trim() === merchant.slug;

  async function handleConfirm() {
    if (!slugMatches) return;
    setBusy(true);
    setError(null);
    try {
      const result: DeleteMerchantResult = await deleteMerchant(merchant.id);

      // AUDIT-PASS-6 M-A: order operations so the user never sees a
      // "Merchant not found" flicker on the hard-delete path.
      //
      // Previous code did invalidate → onClose → navigate. invalidate
      // triggered a refetch of ["merchant", id] that returned 404 →
      // MerchantDetail's guard rendered "Merchant not found" while the
      // modal was unmounting → THEN navigate fired. Visible flash of an
      // error page on an operation that succeeded.
      //
      // Fix: on hard-delete, navigate FIRST (replace, no back button),
      // then drop the cached row entirely (removeQueries — no refetch
      // needed; it's gone), then invalidate the list.
      // On soft-close, the row still exists, so invalidate is the
      // right call there.
      //
      // AUDIT-PASS-6 UI CRITICAL #2: also wire the toast that the modal's
      // copy promises. Each path gets its own message — operators need
      // to know which branch fired (hard-delete vs soft-close).
      onClose();
      if (result.deleted) {
        navigate("/merchants", { replace: true });
        qc.removeQueries({ queryKey: ["merchant", merchant.id] });
        void qc.invalidateQueries({ queryKey: ["merchants"] });
        addToast({
          title: `${merchant.display_name} deleted`,
          variant: "success",
        });
      } else {
        // Same slug-vs-id prefix fix as the save path above.
        void qc.invalidateQueries({ queryKey: ["merchant"] });
        void qc.invalidateQueries({ queryKey: ["merchants"] });
        addToast({
          title: `${merchant.display_name} closed`,
          body:
            "Status set to closed. History is preserved for audit and " +
            "compliance — the merchant cannot be hard-deleted while it has " +
            "any payouts, bank accounts, or memberships.",
          variant: "success",
        });
      }
    } catch (err) {
      // Keep setBusy on the error path; on success we navigate so the
      // modal unmounts and a setBusy(false) afterwards would be wasted
      // work against an unmounted component.
      setBusy(false);
      setError(
        apiErrorMessage(
          err,
          "Could not remove the merchant — check your access and try again.",
        ),
      );
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      title="Remove merchant?"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn danger"
            type="button"
            onClick={handleConfirm}
            disabled={!slugMatches || busy}
          >
            {busy ? "Removing…" : "Remove merchant"}
          </button>
        </>
      }
    >
      <div className="stack-16">
        <div>
          You are about to remove <strong>{merchant.display_name}</strong>{" "}
          (<span className="mono">{merchant.slug}</span>).
        </div>

        <div
          style={{
            background: "var(--bg-2, #f7f7f8)",
            border: "1px solid var(--hair)",
            borderRadius: 8,
            padding: 12,
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--ink-2)",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: "var(--ink)" }}>
            What happens next
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              If this merchant has no bank accounts, payouts, beneficiaries,
              API keys, or memberships, the record will be deleted permanently.
            </li>
            <li>
              Otherwise it will be marked <span className="mono">closed</span>{" "}
              and its history is preserved for audit and compliance. You can
              tell which outcome happened from the toast that appears.
            </li>
          </ul>
        </div>

        <div className="field">
          <label className="label" htmlFor="confirm-slug">
            Type <span className="mono">{merchant.slug}</span> to confirm
          </label>
          <input
            id="confirm-slug"
            className="input"
            type="text"
            value={confirmSlug}
            onChange={(e) => setConfirmSlug(e.target.value)}
            autoComplete="off"
            autoFocus
            disabled={busy}
          />
        </div>

        {error && (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Bank-rail row with a live credential health check ───────────────────────

function MerchantRailRow({
  rail,
  merchantId,
  showProvider,
  showTest,
}: {
  rail: BankAccount;
  merchantId: number;
  /** Provider column is admin-only — merchants see funding rails by
      label only. Hiding it here matches BankAccounts.tsx's render so
      the same merchant gets a consistent view across both surfaces. */
  showProvider: boolean;
  /** "Test connection" is admin-only (PDF review 26 May 26). Merchants
      see the rail row as read-only. Same posture as the dedicated
      /banks page. */
  showTest: boolean;
}) {
  const qc = useQueryClient();
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);

  async function runTest() {
    setTesting(true);
    setResult(null);
    try {
      const r = await verifyBankAccount(merchantId, rail.id);
      setResult(r);
      if (r.ok) {
        // Invalidate BOTH cache shapes: the MerchantDetail-scoped key the
        // detail page reads from, AND the shared per-merchant key that
        // useBankAccounts() (Payouts page, UploadBatchModal) reads from.
        // Without the second invalidation a freshly-verified rail still
        // shows as 'pending' on the Payouts screen until 60s staleTime.
        void qc.invalidateQueries({
          queryKey: ["merchant-bank-accounts", merchantId],
        });
        void qc.invalidateQueries({
          queryKey: ["bank-accounts", merchantId],
        });
      }
    } catch {
      setResult({
        ok: false,
        verify_error: "Request failed — could not reach the backend.",
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <tr>
      <td>
        <div className="cell-name">{rail.label}</div>
        <div className="cell-id">ba_{rail.id}</div>
      </td>
      {showProvider && (
        <td className="dim">{providerLabel(rail.provider)}</td>
      )}
      <td>{rail.currency}</td>
      <td className="dim">{formatDateTime(rail.created_at)}</td>
      <td>
        <StatusPill status={rail.status} />
      </td>
      <td className="right">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          {result && (
            <span
              style={{
                fontSize: 11,
                color: result.ok
                  ? "var(--ok, #1a7f37)"
                  : "var(--warn, #b42318)",
                maxWidth: 220,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={
                result.ok
                  ? "Bank rail credentials authenticated successfully"
                  : result.verify_error
              }
            >
              {result.ok
                ? "✓ Credentials valid"
                : `✕ ${result.verify_error ?? "Credentials failed"}`}
            </span>
          )}
          {showTest && (
            <button
              className="btn ghost sm"
              type="button"
              onClick={runTest}
              disabled={testing}
            >
              {testing ? "Testing…" : "Test connection"}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// AddBeneficiaryModal has moved to @/components/AddBeneficiaryModal so the
// new top-level /beneficiaries page can share the same form. Merchant-role
// users had no sidebar path to this MerchantDetail page; the new route
// fixes that without duplicating the modal.

// ─── Ledger preview card ────────────────────────────────────────────────────
// MerchantDetail shows only a preview (latest 3 entries) — the full form
// + uncapped history live on the dedicated /merchants/:slug/ledger page.
// Same pattern the other sections on this page follow (banks / payouts /
// memberships render preview slices and link to their full pages).

interface LedgerPreviewCardProps {
  merchantId: number;
  merchantSlugOrId: string | number; // for URL construction
  balanceDisplay: BalanceDisplay;
}

const LEDGER_PREVIEW = 3;

function LedgerPreviewCard({
  merchantId,
  merchantSlugOrId,
  balanceDisplay,
}: LedgerPreviewCardProps) {
  const ledgerQuery = useQuery({
    queryKey: ["merchant-ledger", merchantId],
    queryFn: () => listLedgerEntries(merchantId),
    staleTime: 60_000,
  });
  const entries = ledgerQuery.data ?? [];
  const visible = entries.slice(0, LEDGER_PREVIEW);
  const hasMore = entries.length > LEDGER_PREVIEW;
  // BigInt — entries.amount_minor can be number | bigint depending on the
  // json-bigint codec threshold (MAX_SAFE_INTEGER).
  const entrySum = entries.reduce(
    (acc, e) => acc + BigInt(e.amount_minor),
    0n,
  );
  const ledgerHref = `/merchants/${merchantSlugOrId}/ledger`;

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <header
        className="row"
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--hair)",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div className="stack-2">
          <div className="eyebrow">Ledger</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            Operator-managed balance
            {balanceDisplay !== "ledger" && (
              <span
                className="dim"
                style={{ fontSize: 11, marginLeft: 8, fontWeight: 400 }}
              >
                · merchant currently on <code>{balanceDisplay}</code>
              </span>
            )}
          </div>
        </div>
        <div className="flex-1" />
        {entries.length > 0 && (
          <span className="dim mono" style={{ fontSize: 12 }}>
            Σ entries: {fmtCcyMinor(entrySum, "INR")}
          </span>
        )}
        <Link className="btn ghost sm" to={ledgerHref}>
          {hasMore
            ? `View all ${entries.length} entries`
            : entries.length > 0
              ? "View ledger"
              : "Open ledger"}
        </Link>
      </header>
      <div style={{ padding: "12px 16px" }}>
        {ledgerQuery.isLoading ? (
          <div className="dim" style={{ fontSize: 12 }}>
            Loading entries…
          </div>
        ) : ledgerQuery.isError ? (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            Could not load ledger entries.
          </div>
        ) : entries.length === 0 ? (
          <div className="dim" style={{ fontSize: 12 }}>
            No ledger entries yet.
            {balanceDisplay === "ledger" &&
              " The dashboard balance card will show “ledger not initialized” until an entry lands."}
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Kind</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <LedgerEntryRow key={e.id} entry={e} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export function MerchantDetail() {
  const navigate = useNavigate();
  const setCurrentMerchant = useUI((s) => s.setCurrentMerchant);
  const { id: idOrSlug } = useParams<{ id: string }>();
  const authStatus = useAuth((s) => s.status);
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  // Fetch the merchant by id OR slug — backend resolves either form
  // (payouts-backend#8). The resolved int id arrives in merchant.id and is
  // what every downstream query keys on.
  const {
    data: merchant,
    isLoading,
    isError,
  } = useMerchant(idOrSlug);
  const merchantId = merchant?.id;
  // Per-merchant role lookup: a user can be operator of merchant A and
  // viewer of merchant B; we need the role for THIS merchant only.
  const myMembershipRole = useAuth((s) =>
    s.user?.memberships.find((m) => m.merchant_id === merchantId)?.role,
  );
  // canManageBeneficiaries: backend POST /v1/merchants/:id/beneficiaries
  // requires operator+ at the route layer (main.go:339-345). Pre-fix the
  // panel hid the "+ Add" button unless isAdmin, so legitimate merchant
  // operators / owners saw an empty Beneficiaries card with no way to
  // populate it. Mirror the backend gate exactly: admin globally, OR
  // operator/owner on this merchant. Viewers stay read-only.
  const canManageBeneficiaries =
    isAdmin || myMembershipRole === "owner" || myMembershipRole === "operator";
  // ?edit=1 opens the settings modal on arrival. This is what the sidebar
  // "Merchant settings" shortcut and the Ledger page's balance-source link
  // point at, so an operator can reach a setting in one click instead of
  // Merchants → the merchant → Edit. Closing the modal strips the param so
  // Back / refresh doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [editOpen, setEditOpen] = useState(searchParams.get("edit") === "1");
  useEffect(() => {
    if (searchParams.get("edit") === "1") setEditOpen(true);
  }, [searchParams]);

  function closeEdit() {
    setEditOpen(false);
    if (searchParams.get("edit") != null) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }
  const [addBeneOpen, setAddBeneOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);

  // openInSection: switches the merchant switcher to the merchant being
  // drilled into and navigates to the dedicated section page. Without
  // the switcher set first the destination renders whatever merchant
  // was last selected — leading to a "wrong scope" feel on /payouts.
  // For admins the switcher may have been pointing at a different
  // merchant; for merchant users this is normally a no-op because
  // they only have one merchant in scope anyway.
  function openInSection(path: string) {
    if (merchantId != null) {
      setCurrentMerchant(merchantId, merchant?.slug ?? null);
    }
    navigate(path);
  }

  const enabled = authStatus === "authenticated" && merchantId != null;

  const accountsQuery = useQuery({
    queryKey: ["merchant-bank-accounts", merchantId],
    queryFn: () => listBankAccounts(merchantId!),
    enabled,
    staleTime: 60_000,
  });
  const membershipsQuery = useQuery({
    queryKey: ["merchant-memberships", merchantId],
    queryFn: () => listMemberships(merchantId!),
    enabled,
    staleTime: 60_000,
  });
  const apiKeysQuery = useQuery({
    queryKey: ["merchant-api-keys", merchantId],
    queryFn: () => listApiKeys(merchantId!),
    enabled,
    staleTime: 60_000,
  });
  const payoutsQuery = useQuery({
    queryKey: ["merchant-payouts", merchantId],
    queryFn: () => listPayouts(merchantId!, { limit: SECTION_PREVIEW }),
    enabled,
    staleTime: 60_000,
  });
  const beneficiariesQuery = useQuery({
    queryKey: ["merchant-beneficiaries", merchantId],
    queryFn: () => listBeneficiaries(merchantId!),
    enabled,
    staleTime: 60_000,
  });
  const accounts = accountsQuery.data ?? [];
  const memberships = membershipsQuery.data ?? [];
  const apiKeys = apiKeysQuery.data ?? [];
  const payouts = payoutsQuery.data?.payouts ?? [];
  // The server's count of every payout, not the length of the preview page.
  const payoutTotal = payoutsQuery.data?.total ?? 0;
  const beneficiaries = beneficiariesQuery.data ?? [];

  if (isLoading) {
    return (
      <div className="page">
        <Link to="/merchants" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to merchants
        </Link>
        <div className="empty" style={{ marginTop: 24 }}>
          <div className="dim" style={{ fontSize: 13 }}>
            Loading merchant…
          </div>
        </div>
      </div>
    );
  }

  if (isError || !merchant) {
    return (
      <div className="page">
        <Link to="/merchants" className="btn ghost sm">
          <I.ChevronLeft size={12} /> Back to merchants
        </Link>
        <h1 style={{ marginTop: 16 }}>Merchant not found</h1>
        <p className="dim">
          No merchant with id {idOrSlug}, or you don't have access to it.
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <Link to="/merchants" className="btn ghost sm">
        <I.ChevronLeft size={12} /> Back to merchants
      </Link>
      <div className="page-head" style={{ marginTop: 12 }}>
        <div>
          <div className="eyebrow">mer_{merchant.id}</div>
          <h1>{merchant.display_name}</h1>
          <div className="sub">
            <span className="mono">{merchant.slug}</span> · created{" "}
            {formatDateTime(merchant.created_at)}
          </div>
        </div>
        <div className="actions">
          <StatusPill status={merchant.status} />
          {isAdmin && (
            <button
              className="btn"
              type="button"
              onClick={() => setEditOpen(true)}
            >
              <I.Edit size={12} /> Edit
            </button>
          )}
          {isAdmin && (
            <button
              className="btn ghost"
              type="button"
              onClick={() => setRemoveOpen(true)}
              title="Remove this merchant. Hard-deletes when clean; otherwise marks closed."
            >
              <I.Trash size={12} /> Remove
            </button>
          )}
        </div>
      </div>

      {isAdmin && (
        <EditMerchantModal
          merchant={merchant}
          open={editOpen}
          onClose={closeEdit}
        />
      )}

      {isAdmin && (
        <RemoveMerchantModal
          merchant={merchant}
          open={removeOpen}
          onClose={() => setRemoveOpen(false)}
        />
      )}

      {canManageBeneficiaries && (
        <AddBeneficiaryModal
          merchantId={merchantId!}
          rails={accounts}
          open={addBeneOpen}
          onClose={() => setAddBeneOpen(false)}
        />
      )}

      {(() => {
        // Bank rails section — capped preview + "View all → /banks". The
        // dedicated Bank rails page has Add / Rotate / Verify affordances
        // that this preview deliberately doesn't replicate; operators
        // managing rails do it on /banks, this card is just the at-a-
        // glance link for someone landing on the merchant page.
        const visible = accounts.slice(0, SECTION_PREVIEW);
        const hasMore = accounts.length > SECTION_PREVIEW;
        return (
          <ResourceCard
            title="Bank accounts"
            isLoading={accountsQuery.isLoading}
            isError={accountsQuery.isError}
            count={accounts.length}
            emptyText="No bank rails linked to this merchant yet."
            viewAll={
              hasMore
                ? {
                    label: `View all ${accounts.length} rails`,
                    onClick: () => openInSection("/banks"),
                  }
                : undefined
            }
          >
            <table className="tbl">
              <thead>
                <tr>
                  <th>Label</th>
                  {isAdmin && <th>Provider</th>}
                  <th>Currency</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <MerchantRailRow
                    key={a.id}
                    rail={a}
                    merchantId={merchantId!}
                    showProvider={isAdmin}
                    showTest={isAdmin}
                  />
                ))}
              </tbody>
            </table>
          </ResourceCard>
        );
      })()}

      {/* Migration 021 — configurable payout limits. Card is rendered
          for every member (merchant users see read-only); the Edit
          affordance inside is admin-only and the backend enforces the
          same. */}
      {merchantId != null && (
        <PayoutLimitsCard merchantId={merchantId} canEdit={isAdmin} />
      )}

      {/* Cut 2 — payout routing rules. Admin-only. The card surfaces the
          rule list to non-admins as read-only (no Edit/Add buttons render
          when isAdmin=false, but for now the card is hidden entirely to
          merchant users since they can't act on it). */}
      {merchantId != null && isAdmin && (
        <RoutingRulesCard merchantId={merchantId} rails={merchant.bank_accounts ?? []} />
      )}

      {/* Migration 031 — operator-managed ledger (issue #9). Preview only
          on this page; the full form + uncapped history live at the
          dedicated /merchants/:slug/ledger route, reached via the "View
          ledger" link inside the card header. */}
      {merchantId != null && merchant && (
        <LedgerPreviewCard
          merchantId={merchantId}
          merchantSlugOrId={merchant.slug || merchant.id}
          balanceDisplay={merchant.balance_display ?? "live_paysmart"}
        />
      )}

      {(() => {
        const visible = memberships.slice(0, SECTION_PREVIEW);
        const hasMore = memberships.length > SECTION_PREVIEW;
        return (
          <ResourceCard
            title="Team"
            isLoading={membershipsQuery.isLoading}
            isError={membershipsQuery.isError}
            count={memberships.length}
            emptyText="No team members on this merchant yet."
            viewAll={
              hasMore
                ? {
                    label: `View all ${memberships.length} members`,
                    onClick: () => openInSection("/rbac"),
                  }
                : undefined
            }
          >
            <table className="tbl">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Role</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <tr key={m.user_id}>
                    <td className="mono">{m.user_id}</td>
                    <td>
                      <StatusPill status={m.role} />
                    </td>
                    <td className="dim">{formatDateTime(m.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResourceCard>
        );
      })()}

      {(() => {
        const visible = apiKeys.slice(0, SECTION_PREVIEW);
        const hasMore = apiKeys.length > SECTION_PREVIEW;
        return (
          <ResourceCard
            title="API keys"
            isLoading={apiKeysQuery.isLoading}
            isError={apiKeysQuery.isError}
            count={apiKeys.length}
            emptyText="No API keys minted for this merchant."
            viewAll={
              hasMore
                ? {
                    label: `View all ${apiKeys.length} keys`,
                    onClick: () => openInSection("/keys"),
                  }
                : undefined
            }
          >
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Scopes</th>
                  <th>Status</th>
                  <th>Last used</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((k) => (
                  <tr key={k.id}>
                    <td>
                      <div className="cell-name">{k.name}</div>
                      <div className="cell-id">key_{k.id}</div>
                    </td>
                    <td className="mono">{k.prefix}</td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {k.scopes.join(", ")}
                    </td>
                    <td className="dim">{k.status}</td>
                    <td className="dim">
                      {k.last_used_at ? formatDateTime(k.last_used_at) : "—"}
                    </td>
                    <td className="dim">{formatDateTime(k.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResourceCard>
        );
      })()}

      {(() => {
        // Payouts preview — capped + "View all" to /payouts where the
        // full table, search, filters and CSV export live. Rows are
        // clickable for admins (drill into the per-payout detail page);
        // merchants don't get the drill-down (only admins should see
        // provider error / payload).
        const visible = payouts;
        const hasMore = payoutTotal > visible.length;
        return (
          <ResourceCard
            title="Payouts"
            isLoading={payoutsQuery.isLoading}
            isError={payoutsQuery.isError}
            count={payoutTotal}
            emptyText="No payouts for this merchant yet."
            viewAll={
              hasMore
                ? {
                    label: `View all ${payoutTotal.toLocaleString("en-IN")} payouts`,
                    onClick: () => openInSection("/payouts"),
                  }
                : undefined
            }
          >
            <table className="tbl">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Request ID</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr
                    key={p.id}
                    role={isAdmin ? "button" : undefined}
                    tabIndex={isAdmin ? 0 : undefined}
                    onClick={
                      isAdmin
                        ? () => navigate(`/payouts/${p.id}`)
                        : undefined
                    }
                    onKeyDown={
                      isAdmin
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/payouts/${p.id}`);
                            }
                          }
                        : undefined
                    }
                    style={isAdmin ? { cursor: "pointer" } : undefined}
                    title={isAdmin ? `Open pay_${p.id}` : undefined}
                  >
                    <td className="mono">pay_{p.id}</td>
                    <td className="mono">
                      {fmtCcyMinor(p.amount_minor, p.currency)}
                    </td>
                    <td>
                      <StatusPill status={p.status} />
                    </td>
                    <td className="dim">
                      {p.txn_mode === 1
                        ? "IMPS"
                        : p.txn_mode === 2
                          ? "NEFT"
                          : p.txn_mode}
                    </td>
                    <td className="mono dim" style={{ fontSize: 11 }}>
                      {p.request_id}
                    </td>
                    <td className="dim">{formatDateTime(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResourceCard>
        );
      })()}

      {(() => {
        // Beneficiaries preview — capped + "View all" to /beneficiaries
        // where the full searchable list lives. Rows are clickable so an
        // operator drilling into a merchant can jump straight into a
        // beneficiary's payment history. Previously this card carried
        // its own client-side pager; the dedicated page does that job
        // now, and keeping the page inside MerchantDetail too made the
        // section consume a third of the viewport on a merchant with
        // hundreds of payees.
        const visible = beneficiaries.slice(0, SECTION_PREVIEW);
        const hasMore = beneficiaries.length > SECTION_PREVIEW;
        return (
          <ResourceCard
            title="Beneficiaries"
            isLoading={beneficiariesQuery.isLoading}
            isError={beneficiariesQuery.isError}
            count={beneficiaries.length}
            emptyText="No beneficiaries registered for this merchant."
            action={
              canManageBeneficiaries ? (
                <button
                  className="btn ghost sm"
                  type="button"
                  onClick={() => setAddBeneOpen(true)}
                >
                  <I.Plus size={11} /> Add
                </button>
              ) : undefined
            }
            viewAll={
              hasMore
                ? {
                    label: `View all ${beneficiaries.length} beneficiaries`,
                    onClick: () => openInSection("/beneficiaries"),
                  }
                : undefined
            }
          >
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Account</th>
                  <th>IFSC</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((b) => (
                  <tr
                    key={b.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/beneficiaries/${b.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/beneficiaries/${b.id}`);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                    title={`Open ${b.label}`}
                  >
                    <td>
                      <div className="cell-name">{b.label}</div>
                      {/* Bare integer id matches the CSV mass-payout
                          `beneficiary_id` column 1-for-1 — operators
                          copy-paste this directly into the template. */}
                      <div className="cell-id">id: {b.id}</div>
                    </td>
                    <td className="mono">{b.account_no}</td>
                    <td className="mono">{b.ifsc}</td>
                    <td className="dim">{b.is_registered ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResourceCard>
        );
      })()}
    </div>
  );
}
